import { Icon } from "@iconify/react";
import { useApp, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { BillingCycle } from "@next-platform/client-common-core/user/types";
import { Button, Card, CardBody, CardHeader, Spinner } from "@nextui-org/react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CardIcon from "@/components/Checkout/CardIcon";
import Trans from "@/components/Trans/Trans";
import useApi from "@/components/useApi";
import usePurchaseOptions from "@/hooks/usePurchaseOptions";
import { getCurrencySymbol } from "@/utils";
import getBillingCycleFromPlanId from "@/utils/getBillingCycleFromPlanId";

import { ActionResponse, SubscriptionData } from "../types";
import CellWrapper from "./CellWrapper";
import ModalChangeCard from "./ModalChangeCard";
import ModalChangePlan from "./ModalChangePlan";
import ModalConfirmCancel from "./ModalConfirmCancel";

const SubscriberPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { purchaseOptions } = usePurchaseOptions({ initialData: location.state?.purchaseOptions ?? undefined });
  const { t } = useTranslation();
  const { applicationName } = useApp();
  const { callApi, jsonApiService, isLoading: isDataLoading, forceRefreshToken } = useApi<SubscriptionData[]>();
  const { callApi: callActionApi, isLoading: isActioning } = useApi<ActionResponse>();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
  const [isChangeCardModalOpen, setIsChangeCardModalOpen] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [error, setError] = useState("");
  const [chargeRebillError, setChargeRebillError] = useState("");
  const { onlyUserId: user_id } = useUserInfo();
  const date = new Date(subscriptionData?.recur_at ?? "");
  const recurringDate = date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  const onCancelConfirm = () => {
    setError("");
    callActionApi(jsonApiService.updateResource, "subscriptions", "sticky", {
      action: "cancel",
      order_id: subscriptionData?.order_id,
      subscription_id: subscriptionData?.subscription_id,
    })
      .then((res) => {
        if (res?.status === "SUCCESS") {
          setSubscriptionData({ ...subscriptionData!, subscription_status: res.subscription_status ?? subscriptionData!.subscription_status });
          setIsCancelModalOpen(false);
        }
      })
      .catch((e) => {
        setError(e.message);
      });
  };

  const onUncancel = () => {
    setError("");
    callActionApi(jsonApiService.updateResource, "subscriptions", "sticky", {
      action: "uncancel",
      order_id: subscriptionData?.order_id,
      subscription_id: subscriptionData?.subscription_id,
    })
      .then((res) => {
        if (res?.status === "SUCCESS") {
          setSubscriptionData({ ...subscriptionData!, subscription_status: res.subscription_status ?? subscriptionData!.subscription_status });
        }
      })
      .catch((e) => {
        console.log("Error", e.message);
        setError(e.message);
      });
  };

  const loadSubscriptionData = () => {
    callApi(jsonApiService.fetchResource, "subscriptions/sticky")
      .then((data) => {
        setSubscriptionData(data?.[0] ?? null);
      })
      .catch((e) => {
        console.log("Error", e.message);
      });
  };

  useEffect(() => {
    if (user_id) {
      loadSubscriptionData();
    }
  }, [user_id]);

  const onChangePlan = (plan_id: string) => {
    setError("");
    callActionApi(jsonApiService.updateResource, "subscriptions", "sticky", {
      action: "change_plan",
      order_id: subscriptionData?.order_id,
      subscription_id: subscriptionData?.subscription_id,
      plan_id: plan_id,
    })
      .then((res) => {
        if (res?.status !== "SUCCESS") {
          setError(res?.message ?? "");
        }
        loadSubscriptionData();
        setIsChangePlanModalOpen(false);
        //we want to refresh token to get the new permissions after waiting 5 seconds
        setTimeout(() => {
          forceRefreshToken();
        }, 5000);
      })
      .catch((e) => {
        setError(e.message);
        setIsChangePlanModalOpen(false);
      });
  };

  const onChangeCard = (card_token: string) => {
    setError("");
    callActionApi(jsonApiService.updateResource, "subscriptions", "sticky", {
      action: "change_card",
      order_id: subscriptionData?.order_id,
      subscription_id: subscriptionData?.subscription_id,
      card_token: card_token,
    })
      .then((res) => {
        if (res?.status !== "SUCCESS") {
          setError(res?.message ?? "");
        } else {
          loadSubscriptionData();
          setIsChangeCardModalOpen(false);
        }
      })
      .catch((e) => {
        console.log("Error", e.message);
        setError(e.message);
      });
  };

  const onChargeRebill = () => {
    setError("");
    callActionApi(jsonApiService.updateResource, "subscriptions", "sticky", {
      action: "charge_rebill",
      order_id: subscriptionData?.order_id,
      subscription_id: subscriptionData?.subscription_id,
    })
      .then((res) => {
        if (res?.status === "SUCCESS") {
          loadSubscriptionData();
        } else {
          setChargeRebillError(res?.message ?? "");
        }
      })
      .catch((e) => {
        console.log("Error", e.message);
        setChargeRebillError(e.message);
      });
  };

  const role = subscriptionData?.current_product?.role ?? "";
  const isCurrentproductDifferent = subscriptionData?.current_product?.plan_id !== subscriptionData?.next_product?.plan_id;
  const isCancelled = subscriptionData?.subscription_status === "cancelled";
  const isCardDeleted = subscriptionData?.is_card_deleted;
  const isExpired = subscriptionData?.subscription_status === "expired";
  const currentBillingCycle = getBillingCycleFromPlanId(subscriptionData?.next_product?.plan_id);
  const periodLabelByBillingCycle: Record<BillingCycle, string> = {
    [BillingCycle.MONTHLY]: t("subscription:portal.perMonth"),
    [BillingCycle.QUARTERLY]: t("subscription:portal.perQuarter"),
    [BillingCycle.ANNUAL]: t("subscription:portal.perYear"),
  };
  const resolvedBillingCycle = periodLabelByBillingCycle[currentBillingCycle] ?? t("subscription:portal.perMonth");
  const isGracePeriod = subscriptionData?.subscription_status === "grace";
  const tier = subscriptionData?.next_product?.name ?? "";

  return (
    <>
      <Card className="w-full max-w-lg p-2">
        <CardHeader className="flex flex-col items-start px-4 pb-0 pt-4">
          <p className="text-large">
            <Trans i18nKey="subscription:portal.title" />
          </p>
          <p className="text-small text-default-500">
            <Trans i18nKey="subscription:portal.subtitle" />
          </p>
        </CardHeader>
        {isDataLoading && (
          <CardBody className="min-h-96">
            <Spinner label={t("subscription:portal.loading")} />
          </CardBody>
        )}
        {!isDataLoading && (
          <CardBody className="space-y-2 min-h-96">
            <CellWrapper>
              <div>
                <p className="text-xl font-bold">{applicationName}</p>
                <p className="text-large mt-6">{tier}</p>
                <p>
                  <Trans
                    i18nKey="subscription:portal.pricePerPeriod"
                    values={{
                      price: `${getCurrencySymbol(subscriptionData?.next_product?.currency ?? "")}${parseFloat(subscriptionData?.next_product?.price ?? "0").toFixed(2)}`,
                      period: resolvedBillingCycle,
                    }}
                  />
                </p>
                <p className="text-default-500 text-small">
                  <Trans i18nKey="subscription:portal.applicableTaxes" />
                </p>

                {!isGracePeriod && !isExpired && (
                  <p className="text-default-500 mt-8">
                    {isCancelled ? (
                      <Trans i18nKey="subscription:portal.accessEndsOn" values={{ date: recurringDate }} />
                    ) : (
                      <Trans i18nKey="subscription:portal.nextChargeDate" values={{ date: recurringDate }} />
                    )}
                  </p>
                )}
                {!!isExpired && (
                  <p className="text-danger mt-8">
                    <Trans i18nKey="subscription:portal.subscriptionExpired" />
                  </p>
                )}
                {!!isGracePeriod && (
                  <>
                    {!chargeRebillError && (
                      <p className="text-danger mt-8">
                        <Icon icon="emojione-v1:warning" className="mr-2" />
                        <Trans i18nKey="subscription:portal.paymentFailed" />
                      </p>
                    )}
                    {!!chargeRebillError && <p className="text-danger mt-8">{chargeRebillError}</p>}
                    <Button
                      className="mt-4"
                      color="primary"
                      onClick={onChargeRebill}
                      isLoading={isActioning}
                      isDisabled={isActioning}
                      aria-label="pay"
                    >
                      <Trans i18nKey="subscription:portal.payAmountDue" />
                    </Button>
                  </>
                )}
                {!!isCurrentproductDifferent && (
                  <p className="pt-4 text-secondary text-small">
                    <Trans i18nKey="subscription:portal.currentPlan" values={{ plan: subscriptionData?.current_product?.name }} />
                  </p>
                )}
              </div>
              {!!(isCancelled && !isExpired) && (
                <div className="text-danger inline-block align-top">
                  <Trans i18nKey="subscription:portal.cancelled" />
                </div>
              )}
              {!!isExpired && (
                <div className="text-danger inline-block align-top">
                  <Trans i18nKey="subscription:portal.expired" />
                </div>
              )}
            </CellWrapper>
            {subscriptionData?.credit_card?.last_4 && (
              <CellWrapper>
                <div>
                  <p className="text-large">
                    <Trans i18nKey="subscription:portal.paymentMethod" />
                  </p>
                  <p className="text-default-500 flex items-center">
                    <CardIcon className="mt-2 mr-2" width={30} brand={subscriptionData?.credit_card?.type} />{" "}
                    <Trans i18nKey="subscription:portal.endingIn" values={{ last4: subscriptionData?.credit_card?.last_4 }} />
                  </p>
                </div>
                {!isCardDeleted && (
                  <Button radius="full" variant="bordered" onClick={() => setIsChangeCardModalOpen(true)} aria-label="change">
                    <Trans i18nKey="subscription:portal.change" />
                  </Button>
                )}
              </CellWrapper>
            )}
            {!isCardDeleted && !isExpired && (
              <>
                <Button
                  isDisabled={isCancelled}
                  onPress={() => {
                    setIsChangePlanModalOpen(true);
                  }}
                  aria-label="change-plan"
                >
                  <Trans i18nKey="subscription:portal.changePlan" />
                </Button>
                {!isCancelled && (
                  <Button onClick={() => setIsCancelModalOpen(true)} aria-label="cancel-membership">
                    <Trans i18nKey="subscription:portal.cancelMembership" />
                  </Button>
                )}
                {isCancelled && (
                  <Button
                    size="lg"
                    className="text-success"
                    onClick={onUncancel}
                    isDisabled={isActioning}
                    isLoading={isActioning}
                    aria-label="uncancel-membership"
                  >
                    {isActioning ? (
                      <span>
                        <Trans i18nKey="subscription:portal.uncancelling" />
                      </span>
                    ) : (
                      <span>
                        <Trans i18nKey="subscription:portal.uncancelMembership" />
                      </span>
                    )}
                  </Button>
                )}
              </>
            )}
            {isExpired && (
              <Button
                className="text-success"
                onPress={() => {
                  let link = `/checkout`;
                  if (subscriptionData?.next_product?.plan_id) {
                    link += `?plan_id=${subscriptionData?.next_product?.plan_id}`;
                  }
                  navigate(link);
                }}
                aria-label="reactivate"
              >
                <Trans i18nKey="subscription:portal.choosePlanReactivate" />
              </Button>
            )}
            {error && <p className="flex text-danger justify-center">{error}</p>}
          </CardBody>
        )}
      </Card>
      <ModalConfirmCancel
        isOpen={isCancelModalOpen}
        onOpenChange={setIsCancelModalOpen}
        onCancelConfirm={onCancelConfirm}
        role={role}
        isActioning={isActioning}
        recurringDate={recurringDate}
        purchaseOptions={purchaseOptions}
        billingCycle={currentBillingCycle}
      />

      <ModalChangePlan
        isOpen={isChangePlanModalOpen}
        onOpenChange={setIsChangePlanModalOpen}
        onChangePlan={onChangePlan}
        currentPlanId={subscriptionData?.next_product?.plan_id ?? ""}
        availablePlans={subscriptionData?.available_plans ?? []}
        isActioning={isActioning}
        currentBillingCycle={currentBillingCycle}
        purchaseOptions={purchaseOptions}
      />
      <ModalChangeCard
        isOpen={isChangeCardModalOpen}
        onOpenChange={setIsChangeCardModalOpen}
        onChangeCard={onChangeCard}
        isActioning={isActioning}
        error={error}
      />
    </>
  );
};
SubscriberPortal.displayName = "SubscriberPortal";

export default SubscriberPortal;
